/*
 * /admin/operations/pipeline-health — single-page summary of the data
 * pipeline's actual state. Built in v1.7.0 to close the operator-blind-
 * spot identified in docs/FUNCTIONAL_GAP_AUDIT_2026-05-01: 367 successful
 * sync runs producing 4 ClickHouse rows wasn't visible anywhere except
 * via psql. The page surfaces:
 *
 *   - Per-sync-type completed / failed / running / queued / dead-lettered
 *     counts over the last 24 h, plus "last successful X — Y h ago".
 *   - All watermarks across all scopes (so an operator can see the
 *     attempted-cursor low-water-mark even when no events flowed).
 *   - Raw source-row counts in Postgres (tracker_events,
 *     tracker_enrollments, tracked_entities, submitted_aggregate_source_facts)
 *     so the operator can spot the "sync runs but persists nothing"
 *     pattern at a glance.
 *
 * Backed by GET /api/admin/pipeline-health.
 */

import { useEffect, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { api, ApiError } from "../../lib/api.js";

type Health = Awaited<ReturnType<typeof api.pipelineHealth>>;

function relTime(iso: string | null): string {
  if (!iso) return "never";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  const sec = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
}

export default function AdminPipelineHealth() {
  const [health, setHealth] = useState<Health | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  function load() {
    setRefreshing(true);
    api.pipelineHealth()
      .then((h) => { setHealth(h); setError(null); })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setRefreshing(false));
  }

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Pipeline health</h1>
          <p className="muted">
            Cross-tenant view of the data pipeline. Per-sync-type completion stats over
            the last 24 h, watermark cursors, and raw source-row counts. Auto-refreshes
            every 30 s.
          </p>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={load} disabled={refreshing}>
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}
      {!health && !error && <p className="muted">Loading…</p>}

      {health && (
        <>
          <section className="section" style={{ marginBottom: 16 }}>
            <div className="section-head">
              <h2>Sync jobs (last 24 h)</h2>
              <span className="muted" style={{ fontSize: 12 }}>{relTime(health.generatedAt)}</span>
            </div>
            {health.syncs.length === 0 ? (
              <div className="empty-state-card"><p className="muted">No sync activity in the last 24 h.</p></div>
            ) : (
              <table className="transfer-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th style={{ textAlign: "right" }}>Completed</th>
                    <th style={{ textAlign: "right" }}>Failed</th>
                    <th style={{ textAlign: "right" }}>Dead-lettered</th>
                    <th style={{ textAlign: "right" }}>Running</th>
                    <th style={{ textAlign: "right" }}>Queued</th>
                    <th>Last completed</th>
                    <th>Health</th>
                  </tr>
                </thead>
                <tbody>
                  {health.syncs.map((s) => {
                    const total = s.completed + s.failed + s.deadLettered;
                    const successRate = total > 0 ? (s.completed / total) : 1;
                    const cls =
                      successRate >= 0.95 ? "pill pill-ok"
                      : successRate >= 0.5 ? "pill pill-warn"
                      : "pill pill-err";
                    return (
                      <tr key={s.type}>
                        <td className="mono" style={{ fontSize: 12 }}>{s.type}</td>
                        <td style={{ textAlign: "right" }}>{s.completed}</td>
                        <td style={{ textAlign: "right", color: s.failed > 0 ? "#b91c1c" : undefined }}>{s.failed}</td>
                        <td style={{ textAlign: "right", color: s.deadLettered > 0 ? "#b91c1c" : undefined }}>{s.deadLettered}</td>
                        <td style={{ textAlign: "right" }}>{s.running}</td>
                        <td style={{ textAlign: "right" }}>{s.queued}</td>
                        <td className="muted" style={{ fontSize: 12 }} title={s.latestCompletedAt ?? "never"}>
                          {relTime(s.latestCompletedAt)}
                        </td>
                        <td><span className={cls}>{Math.round(successRate * 100)}%</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>

          <section className="section" style={{ marginBottom: 16 }}>
            <div className="section-head"><h2>Source-row counts</h2></div>
            <section className="stat-grid">
              {Object.entries(health.rawRowCounts).map(([table, count]) => (
                <div className="stat" key={table}>
                  <div className="stat-label" style={{ fontSize: 11 }}>{table}</div>
                  <div className="stat-value" style={{ color: count === 0 ? "#b91c1c" : undefined }}>
                    {count.toLocaleString()}
                  </div>
                </div>
              ))}
            </section>
            <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
              Zeros are red-tinted: they often indicate sync runs without persistence. Compare to
              the sync-job table above — successful runs producing zero rows is the
              docs/FUNCTIONAL_GAP_AUDIT_2026-05-01 §2.1 pattern.
            </p>
          </section>

          <section className="section">
            <div className="section-head"><h2>Watermarks</h2></div>
            {health.watermarks.length === 0 ? (
              <div className="empty-state-card">
                <p className="muted">
                  No watermarks set. If sync jobs are completing but no watermark is here,
                  that's the §2.3 pattern (since v1.6.0, attempted-cursor watermarks should
                  always be persisted — empty means no sync has run yet).
                </p>
              </div>
            ) : (
              <table className="transfer-table">
                <thead>
                  <tr>
                    <th>Scope</th>
                    <th>Key</th>
                    <th>Cursor (lastUpdated)</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {health.watermarks.map((w) => (
                    <tr key={`${w.scope}/${w.key}`}>
                      <td className="mono" style={{ fontSize: 11 }}>{w.scope}</td>
                      <td className="mono" style={{ fontSize: 11 }}>{w.key}</td>
                      <td className="mono" style={{ fontSize: 11 }}>{w.lastUpdated}</td>
                      <td className="muted" style={{ fontSize: 12 }} title={w.updatedAt}>
                        {relTime(w.updatedAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}
